import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Sidebar } from "../../../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../../../components/CustomButtonTable/CustomButtonTable";
import { CustomIconButton } from "../../../../components/CustomIconButton/CustomIconButton";
import { Icon } from "../../../../components/Icon/Icon";
import { Box, CircularProgress, Divider, Chip } from "@mui/material";
import dayjs from "dayjs";
import { useGetDocumentsForAnEventOfAVehicle, useGetVehiculesEventsById, useDeleteEvents } from "../../../../api/vehicule";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import useStyles from "./styles";
import { colors } from "../../../../styles/colors";
import toast, { Toaster } from "react-hot-toast";

type Props = {};

/**
 * Download a file via fetch+blob. Falls back to opening in a new tab.
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  if (!url) throw new Error('No URL provided');

  // Ensure filename has an extension
  let finalFilename = filename;
  if (filename && !filename.includes('.')) {
    try {
      const urlPath = url.split('?')[0];
      const lastPart = decodeURIComponent(urlPath.split('/').pop() || '');
      const extMatch = lastPart.match(/\.([a-zA-Z0-9]{1,5})$/);
      if (extMatch) finalFilename = `${filename}.${extMatch[1].toLowerCase()}`;
    } catch { /* keep original name */ }
  }

  const response = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const blob = await response.blob();
  if (blob.size === 0) throw new Error('Empty file received');

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = blobUrl;
  link.download = finalFilename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (link.parentNode) document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}

export const EventDetail: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { id = "", eventId = "" } = useParams<{ id: string; eventId: string }>();
  const vehicleId = parseInt(id, 10);
  const parsedEventId = parseInt(eventId, 10);

  const { event, isLoading, isError } = useGetVehiculesEventsById(vehicleId, parsedEventId);
  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvents(vehicleId, parsedEventId);
  const { documents, isLoading: isLoadingDocuments } = useGetDocumentsForAnEventOfAVehicle(vehicleId, parsedEventId);

  const [downloadingDocs, setDownloadingDocs] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleBack = () => {
    navigate(`/vehicule/update/${vehicleId}`);
  };

  /**
   * Production-ready download handler
   */
  const handleDownloadDocument = async (path: string, name: string, docId: string) => {
    if (!path) {
      toast.error(t("error.noDocumentPath", "Document path not available"));
      return;
    }

    // Prevent multiple simultaneous downloads
    if (downloadingDocs.has(docId)) {
      return;
    }

    setDownloadingDocs(prev => new Set(prev).add(docId));
    const loadingToast = toast.loading(t("event.downloading", "Preparing download..."));

    try {
      await downloadFile(path, name || 'document');
      
      toast.dismiss(loadingToast);
      toast.success(t("event.downloadComplete", "Document downloaded successfully!"));
    } catch (error) {
      toast.dismiss(loadingToast);
      
      // Offer to view instead
      toast.error(
        t("error.downloadFailed", "Download failed. Opening in new tab..."),
        { duration: 3000 }
      );
      
      setTimeout(() => {
        window.open(path, '_blank', 'noopener,noreferrer');
      }, 500);
    } finally {
      setDownloadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  const handleViewDocument = (path: string) => {
    if (!path) {
      toast.error(t("error.noDocumentPath", "Document path not available"));
      return;
    }
    
    const viewWindow = window.open(path, '_blank', 'noopener,noreferrer');
    
    if (!viewWindow) {
      toast.error(t("error.popupBlocked", "Please allow popups to view documents"));
    }
  };

  const getBcStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: "success" | "warning" | "error" | "default" }> = {
      completed: { label: t("event.status.completed", "Completed"), color: "success" },
      pending: { label: t("event.status.pending", "Pending"), color: "warning" },
      cancelled: { label: t("event.status.cancelled", "Cancelled"), color: "error" },
    };

    const config = statusConfig[status] || { label: status, color: "default" };
    
    return (
      <Chip 
        label={config.label} 
        color={config.color}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  if (isNaN(vehicleId) || isNaN(parsedEventId)) {
    return (
      <Sidebar
        styles={{
          sidebarRightRoot: classes.eventDetailRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.errorContainer}>
          <CustomTypography 
            variant="h2" 
            text={t("error.invalidId", "Invalid ID")} 
          />
          <CustomButtonTable
            variant="default"
            label={t("common.goBack", "Go Back")}
            onClick={handleBack}
          />
        </div>
      </Sidebar>
    );
  }

  if (isLoading) {
    return (
      <Sidebar
        styles={{
          sidebarRightRoot: classes.eventDetailRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Sidebar>
    );
  }

  if (isError || !event || !event.id) {
    return (
      <Sidebar
        styles={{
          sidebarRightRoot: classes.eventDetailRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.errorContainer}>
          <CustomTypography 
            variant="h2" 
            text={t("error.loadingEvent", "Error loading event details")} 
          />
          <CustomButtonTable
            variant="default"
            label={t("common.goBack", "Go Back")}
            onClick={handleBack}
          />
        </div>
      </Sidebar>
    );
  }

  const documentList = Array.isArray(documents) ? documents : [];

  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
          },
          error: {
            duration: 5000,
          },
        }}
      />
      <Sidebar
        styles={{
          sidebarRightRoot: classes.eventDetailRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.container}>
          {/* Header Section */}
          <div className={classes.headerSection}>
            <div className={classes.headerLeft}>
              <CustomIconButton
                iconName="arrowLeft"
                widthIcon="20px"
                heightIcon="20px"
                onClick={handleBack}
              />
              <div className={classes.titleSection}>
                <CustomTypography
                  variant="h1"
                  text={event.name || t("event.eventDetails", "Event Details")}
                  lineHeight="47px"
                />
                <div className={classes.vehicleInfo}>
                  <Icon name="vehicule" height={24} width={24} />
                  <CustomTypography 
                    variant="default" 
                    text={`${t("vehicule.vehicule", "Vehicle")} ID: ${vehicleId}`}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <CustomButtonTable
                variant="default"
                textVariant="link"
                label={isDeleting ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
                width="fit-content"
                fontWeight="bold"
                textDecoration="none"
                iconName="trash"
                disabled={isDeleting}
                onClick={() => setDeleteModalOpen(true)}
              />
            </div>
          </div>

          <CustomModal
            open={deleteModalOpen}
            title={t("event.deleteEvent", "Delete Event")}
            isConfirmation
            textConfirmation={t("common.confirm", "Confirm")}
            loadingSave={isDeleting}
            onClose={() => setDeleteModalOpen(false)}
            onCancel={() => setDeleteModalOpen(false)}
            onConfirm={() => {
              deleteEvent(undefined, {
                onSuccess: () => {
                  setDeleteModalOpen(false);
                  navigate(`/vehicule/update/${vehicleId}`);
                },
              });
            }}
          >
            <div style={{ padding: '8px 4px 16px 4px' }}>
              <CustomTypography
                variant="default"
                text={t("event.deleteConfirm", "Are you sure you want to delete this event? This action cannot be undone.")}
              />
            </div>
          </CustomModal>

          {/* Event Details Card */}
          <div className={classes.detailsCard}>
            <Box sx={{ padding: '24px' }}>
              <CustomTypography
                variant="h3"
                text={t("event.generalInformation", "General Information")}
                styles={{ marginBottom: '20px' }}
              />

              <div className={classes.infoGrid}>
                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.eventName", "Event Name")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={event.name || "-"}
                    styles={{ fontWeight: 500 }}
                  />
                </div>

                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.eventType", "Event Type")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={event?.event_type?.libelle || "-"}
                    styles={{ fontWeight: 500 }}
                  />
                </div>

                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.dateTime", "Date & Time")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={event.datetime ? dayjs(event.datetime).format("DD/MM/YYYY HH:mm") : "-"}
                    styles={{ fontWeight: 500 }}
                  />
                </div>

                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.mileage", "Mileage")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={event.mileage ? `${event.mileage} km` : "-"}
                    styles={{ fontWeight: 500 }}
                  />
                </div>

                {event.bc_status && (
                  <div className={classes.infoItem}>
                    <CustomTypography
                      variant="link"
                      text={t("event.statusLabel", "Status")}
                      styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                    />
                    <Box sx={{ marginTop: '8px' }}>
                      {getBcStatusChip(event.bc_status)}
                    </Box>
                  </div>
                )}

                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.eventId", "Event ID")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={`#${event.id}`}
                    styles={{ fontWeight: 500 }}
                  />
                </div>

                <div className={classes.infoItem}>
                  <CustomTypography
                    variant="link"
                    text={t("event.eventAddress", "Event Address")}
                    styles={{ color: colors.textSecondary, marginBottom: '8px' }}
                  />
                  <CustomTypography
                    variant="default"
                    text={event?.address?.raw || "-"}
                    styles={{ fontWeight: 500 }}
                  />
                </div>
              </div>
            </Box>

            <Divider />

            {/* Documents Section */}
            <Box sx={{ padding: '24px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <CustomTypography
                  variant="h3"
                  text={t("event.documents", "Documents")}
                />
                {isLoadingDocuments && <CircularProgress size={20} />}
                {!isLoadingDocuments && documentList.length > 0 && (
                  <Chip 
                    label={`${documentList.length}`} 
                    size="small"
                    sx={{ 
                      backgroundColor: colors.primaryLight,
                      color: colors.primary,
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>

              {isLoadingDocuments ? (
                <Box className={classes.emptyState}>
                  <CircularProgress />
                  <CustomTypography
                    variant="default"
                    text={t("event.loadingDocuments", "Loading documents...")}
                    styles={{ color: colors.textSecondary }}
                  />
                </Box>
              ) : documentList.length > 0 ? (
                <div className={classes.documentsGrid}>
                  {documentList.map((doc: any, index: number) => {
                    const docId = doc.id || `doc-${index}`;
                    const isDownloading = downloadingDocs.has(docId);
                    
                    return (
                      <div key={docId} className={classes.documentCard}>
                        <div className={classes.documentInfo}>
                          <Icon name="file" width="32px" height="32px" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <CustomTypography
                              variant="default"
                              text={doc.name || `Document ${index + 1}`}
                              styles={{ 
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            />
                            {doc.type && (
                              <CustomTypography
                                variant="link"
                                text={doc.type}
                                styles={{ color: colors.textSecondary, fontSize: '12px' }}
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className={classes.documentActions}>
                          {(doc.path || doc.scan_id) && (
                            <>
                              <CustomButtonTable
                                variant="secondary"
                                textVariant="link"
                                label={t("common.view", "View")}
                                iconName="eye"
                                width="fit-content"
                                onClick={() => handleViewDocument(doc.path || doc.scan_id)}
                              />
                              <CustomButtonTable
                                variant="secondary"
                                textVariant="link"
                                label={isDownloading ? t("event.downloading", "Downloading...") : t("common.download", "Download")}
                                iconName="download"
                                width="fit-content"
                                disabled={isDownloading}
                                onClick={() => handleDownloadDocument(
                                  doc.path || doc.scan_id, 
                                  doc.name || 'document',
                                  docId
                                )}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Box className={classes.emptyState}>
                  <Icon name="file" width="48px" height="48px" />
                  <CustomTypography
                    variant="default"
                    text={t("event.noDocuments", "No documents available for this event")}
                    styles={{ color: colors.textSecondary }}
                  />
                </Box>
              )}
            </Box>
          </div>
        </div>
      </Sidebar>
    </>
  );
};